/*
 * Custom Nails by Maddie — theme scripts
 * Vanilla JS, no dependencies. Everything degrades to plain HTML forms and links without JS.
 */
(() => {
  'use strict';

  const theme = window.theme || {};
  const KIT_KEY = 'cnm:kit';

  /* ---------- small helpers ---------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const store = {
    get(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ } }
  };

  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const money = (cents) => {
    const whole = cents % 100 === 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency,
      minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2
    }).format(cents / 100);
  };

  let toastTimer;
  const toast = (message) => {
    const el = $('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 2800);
  };

  const getKit = () => store.get(KIT_KEY);
  const setKit = (kit) => { if (kit) store.set(KIT_KEY, kit); };

  /* ---------- drawers (<dialog>) : menu + bag ---------- */
  const lockScroll = (on) => { document.documentElement.style.overflow = on ? 'hidden' : ''; };

  const openDrawer = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    lockScroll(true);
  };

  const wireDialog = (dialog) => {
    if (!dialog || dialog._wired) return;
    dialog._wired = true;
    dialog.addEventListener('close', () => {
      lockScroll(false);
      if (dialog._opener && typeof dialog._opener.focus === 'function') dialog._opener.focus();
    });
    // click on the backdrop closes
    dialog.addEventListener('click', (e) => {
      if (e.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) dialog.close();
    });
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-drawer]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-open-drawer'));
      if (dialog && typeof dialog.showModal === 'function') {
        e.preventDefault();
        wireDialog(dialog);
        openDrawer(dialog, opener);
      }
      return;
    }
    const closer = e.target.closest('[data-close-drawer]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) { e.preventDefault(); dialog.close(); }
    }
  });

  /* ---------- cart ---------- */
  const Cart = {
    dialog: () => document.getElementById('CartDrawer'),

    render(sections) {
      if (!sections || !sections['cart-drawer']) return;
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = doc.querySelector('[data-cart-inner]');
      const current = $('[data-cart-inner]');
      if (fresh && current) current.replaceWith(fresh);
      const count = fresh ? fresh.getAttribute('data-cart-count') : null;
      if (count !== null) {
        $$('[data-cart-count]').forEach((el) => {
          if (el.hasAttribute('data-cart-inner')) return;
          el.textContent = count;
        });
        $$('[data-cart-label]').forEach((el) => { el.setAttribute('aria-label', el.getAttribute('data-cart-label').replace('__N__', count)); });
      }
    },

    open(opener) {
      const dialog = this.dialog();
      if (!dialog || typeof dialog.showModal !== 'function') { window.location.href = theme.routes.cart; return; }
      wireDialog(dialog);
      openDrawer(dialog, opener);
    },

    async change(line, quantity) {
      const res = await fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || theme.strings.error);
      this.render(data.sections);
    }
  };

  // quantity buttons + remove inside the drawer
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-line]');
    if (!btn || !btn.closest('#CartDrawer')) return;
    e.preventDefault();
    const line = Number(btn.getAttribute('data-cart-line'));
    const qty = Number(btn.getAttribute('data-cart-qty'));
    btn.setAttribute('aria-busy', 'true');
    try {
      await Cart.change(line, qty);
      const first = $('#CartDrawer [data-cart-line]') || $('#CartDrawer .drawer__close');
      if (first) first.focus();
    } catch (err) {
      toast(err.message);
      btn.removeAttribute('aria-busy');
    }
  });

  /* ---------- <product-form> : add to bag (product page + quick add) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      if (this.dataset.kits) this.applyKit();
    }

    applyKit() {
      let map;
      try { map = JSON.parse(this.dataset.kits); } catch (e) { return; }
      const kit = getKit();
      const input = this.form.querySelector('input[name="id"]');
      if (kit && map[kit] && input) input.value = map[kit];
    }

    async onSubmit(e) {
      if (theme.cartType === 'page') return; // native POST → /cart
      e.preventDefault();
      if (this.dataset.kits) this.applyKit();
      const button = this.form.querySelector('[type="submit"]');
      const label = button ? button.innerHTML : '';
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }

      const body = new FormData(this.form);
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);

      try {
        const res = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
        Cart.render(data.sections);
        Cart.open(button);
        const title = data.product_title || data.title || '';
        toast(theme.strings.added.replace('__TITLE__', title));
      } catch (err) {
        toast(err.message || theme.strings.error);
      } finally {
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); button.innerHTML = label; }
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- <product-info> : size-kit picker on the product page ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      if (!json) return;
      try { this.variants = JSON.parse(json.textContent); } catch (e) { return; }
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[name="id"]')) this.update(true);
      });
      const hasParam = new URL(window.location.href).searchParams.has('variant');
      const kit = getKit();
      if (!hasParam && kit) {
        const match = this.variants.find((v) => v.option1 === kit);
        const radio = match && this.querySelector(`input[name="id"][value="${match.id}"]`);
        if (radio && !radio.checked) { radio.checked = true; }
      }
      this.update(false);
    }

    update(fromUser) {
      const checked = this.querySelector('input[name="id"]:checked');
      if (!checked) return;
      const variant = this.variants.find((v) => String(v.id) === checked.value);
      if (!variant) return;
      $$('[data-product-price]').forEach((el) => { el.textContent = money(variant.price); });
      $$('[data-plan-price]').forEach((el) => { el.textContent = money(variant.plan_price != null ? variant.plan_price : variant.price); });
      const fit = this.querySelector('[data-fit]');
      if (fit) {
        const isFit = /custom/i.test(variant.option1 || '');
        fit.hidden = !isFit;
        $$('input', fit).forEach((input) => { input.disabled = !isFit; });
      }
      if (fromUser) {
        setKit(variant.option1);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* ---------- <size-finder> ---------- */
  class SizeFinder extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-finder-copy]');
      if (!data) return;
      try { this.copy = JSON.parse(data.textContent); } catch (e) { return; }
      this.thumb = this.querySelector('input[name="thumb"]');
      this.index = this.querySelector('input[name="index"]');
      this.kitEl = this.querySelector('[data-finder-kit]');
      this.noteEl = this.querySelector('[data-finder-note]');
      this.action = this.querySelector('[data-finder-action]');
      [this.thumb, this.index].forEach((el) => {
        if (!el) return;
        el.addEventListener('input', () => this.update());
        el.addEventListener('change', () => this.update());
      });
      if (this.action) {
        this.action.addEventListener('click', (e) => {
          setKit(this.kitName);
          if (this.dataset.mode === 'remember') {
            e.preventDefault();
            toast(theme.strings.kitSaved.replace('__KIT__', this.kitName));
          }
        });
      }
      this.update();
    }

    recommend(t, i) {
      if (!t || !i) return 'empty';
      if (t > 18.5 || i > 14) return 'custom';
      if (t < 14.5 && i < 11) return 'petite';
      if (t > 17 || i > 12.8) return 'wide';
      return 'standard';
    }

    update() {
      const key = this.recommend(parseFloat(this.thumb.value), parseFloat(this.index.value));
      const kitKey = key === 'empty' ? 'standard' : key;
      this.kitName = this.copy.kits[kitKey];
      this.kitEl.textContent = this.kitName;
      this.noteEl.textContent = this.copy.notes[key];
      if (this.action) {
        const template = this.dataset.mode === 'remember' ? this.copy.use : this.copy.shop;
        this.action.textContent = template.replace('__KIT__', this.kitName);
      }
    }
  }
  if (!customElements.get('size-finder')) customElements.define('size-finder', SizeFinder);

  /* ---------- <sort-grid> : client-side sort, so no ?sort_by= URLs ever exist ---------- */
  class SortGrid extends HTMLElement {
    connectedCallback() {
      const select = this.querySelector('select');
      const list = document.getElementById(this.dataset.target);
      if (!select || !list) return;
      this.hidden = false;
      const items = Array.from(list.children);
      items.forEach((li, i) => { li.dataset.order = i; });
      select.addEventListener('change', () => {
        const mode = select.value;
        const sorted = items.slice().sort((a, b) => {
          if (mode === 'price-asc') return Number(a.dataset.price) - Number(b.dataset.price);
          if (mode === 'price-desc') return Number(b.dataset.price) - Number(a.dataset.price);
          if (mode === 'az') return (a.dataset.title || '').localeCompare(b.dataset.title || '');
          return Number(a.dataset.order) - Number(b.dataset.order);
        });
        sorted.forEach((li) => list.appendChild(li));
      });
    }
  }
  if (!customElements.get('sort-grid')) customElements.define('sort-grid', SortGrid);

  /* ---------- <custom-builder> : live summary + estimate on the custom page ---------- */
  class CustomBuilder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.min = Number(this.dataset.min) || 0;
      this.max = Number(this.dataset.max) || 0;
      this.form.addEventListener('change', () => this.update());
      this.form.addEventListener('input', () => this.update());

      const set = new URL(window.location.href).searchParams.get('set');
      const select = this.form.querySelector('[data-start-from]');
      if (set && select) {
        const option = Array.from(select.options).find((o) => o.dataset.handle === set);
        if (option) select.value = option.value;
      }
      const date = this.form.querySelector('input[type="date"]');
      if (date) date.min = new Date().toISOString().slice(0, 10);
      this.update();
    }

    picked(name) {
      const el = this.form.querySelector(`input[name="contact[${name}]"]:checked`);
      return el ? el.value : '';
    }

    update() {
      const addons = Array.from(this.form.querySelectorAll('input[data-addon]:checked'));
      const extra = addons.reduce((sum, el) => sum + Number(el.dataset.addon || 0), 0);
      const low = this.min + extra;
      const high = this.max + extra;
      const range = `${money(low * 100)}–${money(high * 100)}`;
      const set = (sel, text) => { const el = this.querySelector(sel); if (el) el.textContent = text; };
      set('[data-sum-shape]', this.picked('Shape'));
      set('[data-sum-length]', this.picked('Length'));
      set('[data-sum-finish]', this.picked('Finish'));
      set('[data-sum-addons]', addons.length ? addons.map((a) => a.dataset.label).join(', ') : this.dataset.none);
      set('[data-estimate]', range);
      set('[data-estimate-note]', addons.length ? this.dataset.noteWith : this.dataset.noteBase);
      const hidden = this.form.querySelector('[data-estimate-input]');
      if (hidden) hidden.value = range;
    }
  }
  if (!customElements.get('custom-builder')) customElements.define('custom-builder', CustomBuilder);

  /* ---------- <sticky-cta> : mobile bottom bar ---------- */
  class StickyCta extends HTMLElement {
    connectedCallback() {
      const watch = this.dataset.watch ? document.querySelector(this.dataset.watch) : null;
      const footer = document.querySelector('.site-footer');
      this.classList.add('is-off');
      if (!('IntersectionObserver' in window)) { this.classList.remove('is-off'); return; }
      let watchVisible = !!watch;
      let footerVisible = false;
      const threshold = Number(this.dataset.after || 0);
      const apply = () => {
        const scrolledEnough = window.scrollY > threshold;
        const show = !footerVisible && (watch ? !watchVisible : scrolledEnough);
        this.classList.toggle('is-off', !show);
      };
      if (watch) {
        new IntersectionObserver(([entry]) => { watchVisible = entry.isIntersecting; apply(); }).observe(watch);
      }
      if (footer) new IntersectionObserver(([entry]) => { footerVisible = entry.isIntersecting; apply(); }).observe(footer);
      if (!watch) window.addEventListener('scroll', apply, { passive: true });
      apply();
    }
  }
  if (!customElements.get('sticky-cta')) customElements.define('sticky-cta', StickyCta);

  /* ---------- <club-options> : Join the club / Buy once on the club page ---------- */
  // "Buy once" posts no selling plan; the hidden Design property is sent only then, so it never
  // rides along on a subscription (where every renewal would repeat it).
  class ClubOptions extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => { if (e.target.name === 'selling_plan') this.update(); });
      this.update();
    }

    update() {
      const checked = this.querySelector('input[name="selling_plan"]:checked');
      const mode = checked && checked.value === '' ? 'once' : 'join';
      $$('[data-club-show]').forEach((el) => { el.hidden = el.dataset.clubShow !== mode; });
      $$('[data-club-once-input]', this).forEach((input) => { input.disabled = mode !== 'once'; });
    }
  }
  if (!customElements.get('club-options')) customElements.define('club-options', ClubOptions);

  /* ---------- sticky PDP button submits the main product form ---------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-submit-form]');
    if (!trigger) return;
    const form = document.getElementById(trigger.getAttribute('data-submit-form'));
    if (!form) return;
    e.preventDefault();
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  });

  /* ---------- club retired wall: "See all", and tap a design to see it large ---------- */
  $$('[data-club-wall-toggle]').forEach((btn) => {
    const wall = btn.parentElement.querySelector('[data-club-wall]');
    if (!wall) return;
    const more = btn.textContent;
    btn.hidden = false;
    btn.addEventListener('click', () => {
      const open = wall.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? btn.getAttribute('data-less') : more;
    });
  });

  document.addEventListener('click', (e) => {
    const card = e.target.closest('[data-club-zoom]');
    if (!card) return;
    const box = card.closest('section') && card.closest('section').querySelector('[data-club-lightbox]');
    if (!box || typeof box.showModal !== 'function' || !card.dataset.src) return;
    const img = document.createElement('img');
    img.src = card.dataset.src;
    img.alt = (card.querySelector('img') || {}).alt || '';
    box.querySelector('[data-club-lightbox-media]').replaceChildren(img);
    box.querySelector('[data-club-lightbox-name]').textContent = card.dataset.name || '';
    box.querySelector('[data-club-lightbox-month]').textContent = card.dataset.month || '';
    box.querySelector('[data-club-lightbox-desc]').textContent = card.dataset.desc || '';
    wireDialog(box);
    openDrawer(box, card);
  });

  /* ---------- wire dialogs present at load ---------- */
  $$('dialog.drawer').forEach(wireDialog);
})();
